(() => {
  'use strict';
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const previousFocus = new WeakMap();
  const showDialog = (dialog, opener) => {
    previousFocus.set(dialog, opener || document.activeElement);
    if (!dialog.open) dialog.showModal();
  };
  const closeDialog = dialog => {
    dialog.querySelectorAll('video').forEach(video => video.pause());
    dialog.close();
    previousFocus.get(dialog)?.focus();
  };
  window.HearthUI = {
    showDialog, closeDialog,
    toast(message) {
      const toast = document.querySelector('[data-toast]');
      if (!toast) return;
      toast.textContent = message;
      toast.hidden = false;
      clearTimeout(toast.timeout);
      toast.timeout = setTimeout(() => { toast.hidden = true; }, 6000);
    },
    celebrate(anchor) {
      if (reducedMotion) return;
      const bounds = anchor.getBoundingClientRect();
      for (let i = 0; i < 18; i++) {
        const particle = document.createElement('span');
        particle.className = 'celebration-particle';
        particle.textContent = ['🎉', '✨', '🧡'][i % 3];
        particle.setAttribute('aria-hidden', 'true');
        particle.style.left = `${bounds.left + bounds.width / 2}px`;
        particle.style.top = `${bounds.top + bounds.height / 2}px`;
        particle.style.setProperty('--dx', `${(Math.random() - .5) * 260}px`);
        particle.style.setProperty('--dy', `${50 + Math.random() * 160}px`);
        document.body.append(particle);
        setTimeout(() => particle.remove(), 1500);
      }
    }
  };
  // Warm small loops before they enter view; keep the first screen focused on the hero.
  function loadLoop(video) {
    let changed = false;
    [video, ...video.querySelectorAll('source[data-src]')].forEach(source => {
      if (source.dataset.src) { source.src = source.dataset.src; delete source.dataset.src; changed = true; }
    });
    if (changed) video.load();
  }
  function initialise(root = document) {
    root.querySelectorAll('[data-gallery]:not([data-ready])').forEach(gallery => {
      gallery.dataset.ready = 'true';
      const track = gallery.querySelector('[data-gallery-track]');
      const slides = [...track.querySelectorAll('[data-gallery-slide]')];
      let variantSlide=null;
      const thumbs = gallery.querySelector('[data-gallery-thumbnails]');
      const previous = gallery.querySelector('[data-gallery-prev]');
      const next = gallery.querySelector('[data-gallery-next]');
      let selected = 0, navigating=false, trackWidth=track.clientWidth;
      const slideLeft=index=>(slides[index]?.offsetLeft||0)-(slides[0]?.offsetLeft||0);
      function go(index,animate=true) {
        selected = Math.max(0, Math.min(slides.length - 1, index));navigating=true;
        track.scrollTo({left:slideLeft(selected),behavior:animate && !reducedMotion?'smooth':'instant'});
        state();
      }
      const addThumbnail=(slide,index)=>{
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('aria-label', `View photo ${index + 1}`);
        const source = slide.querySelector('img,video');
        const image = document.createElement('img');
        const thumbnail = slide.dataset.thumbnail || (source?.tagName === 'VIDEO' ? source.getAttribute('poster') : source?.currentSrc || source?.getAttribute('src'));
        if (thumbnail) image.src = thumbnail;
        else button.textContent = `Media ${index + 1}`;
        if (!slide.dataset.thumbnail && source?.tagName === 'IMG' && source.getAttribute('srcset')) {
          image.srcset = source.getAttribute('srcset');
          image.sizes = '60px';
        }
        image.alt = '';
        image.loading = 'lazy';
        image.width = image.height = 60;
        if (image.getAttribute('src')) button.append(image);
        button.addEventListener('click', () => go(index));
        thumbs.append(button);
      };
      slides.forEach(addThumbnail);
      function state() {
        if (!slides.length) return;
        if(track.clientWidth!==trackWidth)return;
        if(navigating && Math.abs(track.scrollLeft-slideLeft(selected))<2)navigating=false;
        if(!navigating)selected=slides.reduce((best,slide,i)=>Math.abs(slideLeft(i)-track.scrollLeft)<Math.abs(slideLeft(best)-track.scrollLeft)?i:best,0);
        [...thumbs.children].forEach((button,index) => button.setAttribute('aria-current', index === selected ? 'true':'false'));
        previous.disabled = selected === 0;
        next.disabled = selected === slides.length - 1;
      }
      previous.addEventListener('click',()=>go(selected-1));
      next.addEventListener('click',()=>go(selected+1));
      track.addEventListener('scroll', state, {passive:true});
      track.addEventListener('pointerdown',()=>{navigating=false;},{passive:true});
      track.addEventListener('wheel',()=>{navigating=false;},{passive:true});
      new ResizeObserver(()=>{const width=track.clientWidth;if(width!==trackWidth){trackWidth=width;go(selected,false);}}).observe(track);
      gallery.addEventListener('show-variant', event => {
        const detail=event.detail || {};
        const variantId=String(detail.variantId || '');
        const normalize=url=>{try{return new URL(url,location.href).pathname;}catch{return String(url||'');}};
        let index=slides.findIndex(slide=>slide!==variantSlide && (
          (detail.mediaId && slide.dataset.mediaId===String(detail.mediaId)) ||
          (variantId && (slide.dataset.variantIds || '').split(',').map(id=>id.trim()).includes(variantId)) ||
          (detail.image && normalize(slide.querySelector('img')?.getAttribute('src'))===normalize(detail.image))
        ));
        if(index<0){
          // Keep the eight marketing roles intact. One reusable native-variant
          // frame is added only when a variant image is not already in the gallery.
          if(!variantSlide){
            variantSlide=document.createElement('div');variantSlide.className='gallery-slide';variantSlide.dataset.gallerySlide='';variantSlide.dataset.variantPreview='';
            const button=document.createElement('button');button.type='button';button.className='media-open';button.dataset.imageOpen='';
            const img=document.createElement('img');img.width=1400;img.height=1400;button.append(img);variantSlide.append(button);track.append(variantSlide);slides.push(variantSlide);
            addThumbnail(variantSlide,slides.length-1);
          }
          variantSlide.dataset.variantIds=variantId;
          const img=variantSlide.querySelector('img');img.hidden=!detail.image;if(detail.image)img.src=detail.image;else img.removeAttribute('src');img.alt=detail.alt || 'Selected product option';
          let missing=variantSlide.querySelector('[data-photo-unavailable]');if(!missing){missing=document.createElement('p');missing.dataset.photoUnavailable='';missing.textContent='Photo unavailable for this option.';variantSlide.append(missing);}missing.hidden=Boolean(detail.image);variantSlide.querySelector('button').disabled=!detail.image;
          variantSlide.querySelector('button').setAttribute('aria-label',`View ${img.alt}`);
          index=slides.indexOf(variantSlide);
          const thumb=thumbs.children[index];let thumbnail=thumb.querySelector('img');
          if(!thumbnail){thumbnail=document.createElement('img');thumbnail.alt='';thumbnail.width=thumbnail.height=60;thumb.replaceChildren(thumbnail);}
          thumbnail.hidden=!detail.image;if(detail.image)thumbnail.src=detail.image;else thumbnail.removeAttribute('src');thumb.setAttribute('aria-label',`View ${img.alt}`);
        }
        if(index>=0)go(index);
      });
      state();
    });
    root.querySelectorAll('[data-quote-carousel]:not([data-ready])').forEach(carousel=>{
      carousel.dataset.ready='true';
      const slides=[...carousel.querySelectorAll('[data-quote-slide]')];
      if(!slides.length){carousel.hidden=true;return;}
      let index=0,paused=reducedMotion,timer,moving=false;
      const track=carousel.querySelector('[data-quote-track]');
      let position=1;
      if(track && slides.length>1){
        const before=slides.at(-1).cloneNode(true),after=slides[0].cloneNode(true);
        [before,after].forEach(clone=>{clone.removeAttribute('data-quote-slide');clone.setAttribute('aria-hidden','true');clone.inert=true;});
        track.prepend(before);track.append(after);
      }else position=0;
      const display=()=>{
        slides.forEach((slide,i)=>{slide.hidden=false;slide.setAttribute('aria-hidden',String(i!==index));slide.inert=i!==index;slide.setAttribute('role','group');slide.setAttribute('aria-label',`${i+1} of ${slides.length}`);});
        if(track)track.style.transform=`translateX(-${position*100}%)`;
      };
      const settle=()=>{
        moving=false;
        if(!track || slides.length<2)return;
        if(position===0 || position===slides.length+1){
          position=index+1;track.style.transition='none';display();
          requestAnimationFrame(()=>requestAnimationFrame(()=>track.style.transition=''));
        }
      };
      track?.addEventListener('transitionend',event=>{if(event.propertyName==='transform')settle();});
      const change=delta=>{
        if(moving || slides.length<2)return;
        index=(index+delta+slides.length)%slides.length;
        position=reducedMotion?index+1:position+delta;
        moving=!reducedMotion;display();if(reducedMotion)settle();
      };
      const begin=()=>{clearInterval(timer);if(!paused && !document.hidden && !carousel.contains(document.activeElement) && !carousel.matches(':hover') && slides.length>1)timer=setInterval(()=>change(1),6000);};
      carousel.querySelector('[data-quote-previous]').addEventListener('click',()=>{change(-1);begin();});
      carousel.querySelector('[data-quote-next]').addEventListener('click',()=>{change(1);begin();});
      carousel.querySelector('[data-quote-pause]').addEventListener('click',event=>{paused=!paused;event.currentTarget.textContent=paused?'▶':'Ⅱ';event.currentTarget.setAttribute('aria-label',paused?'Start review rotation':'Pause review rotation');begin();});
      carousel.addEventListener('mouseenter',()=>clearInterval(timer));carousel.addEventListener('mouseleave',begin);
      carousel.addEventListener('focusin',()=>clearInterval(timer));carousel.addEventListener('focusout',()=>setTimeout(()=>{if(!carousel.contains(document.activeElement))begin();},0));
      if(slides.length<2)carousel.querySelector('.quote-controls').hidden=true;
      if(track)track.style.transition='none';display();requestAnimationFrame(()=>{if(track)track.style.transition='';});begin();
    });
    const preloader=new IntersectionObserver(entries=>entries.forEach(({target,isIntersecting})=>{if(isIntersecting){target.preload='auto';loadLoop(target);preloader.unobserve(target);}}),{rootMargin:'900px 0px',threshold:0});
    const observer = new IntersectionObserver(entries => entries.forEach(({target,isIntersecting}) => {
      target.dataset.inView = isIntersecting ? 'true' : 'false';
      if (isIntersecting && !reducedMotion && !document.hidden && target.dataset.userPaused !== 'true') {
        loadLoop(target);
        target.play().catch(() => { target.closest('.loop-media')?.classList.add('needs-play'); });
      } else target.pause();
    }), {threshold:.01});
    root.querySelectorAll('video[data-autoplay]:not([data-observed])').forEach(video => {
      video.dataset.observed = 'true'; video.muted = true; video.playsInline = true;
      const button = video.closest('.loop-media')?.querySelector('[data-loop-toggle]');
      const update = () => { if(button){button.textContent=video.paused?'▶':'Ⅱ';button.setAttribute('aria-label',video.paused?'Play video':'Pause video');} };
      video.addEventListener('playing',()=>{video.closest('.loop-media')?.classList.remove('needs-play');video.closest('.loop-media')?.classList.add('is-playing');update();});
      video.addEventListener('pause',update);
      if(reducedMotion){video.autoplay=false;video.pause();video.closest('.loop-media')?.classList.add('needs-play');}
      button?.addEventListener('click',()=>{if(video.paused){video.dataset.userPaused='false';loadLoop(video);video.play().catch(()=>{video.controls=true;});}else{video.dataset.userPaused='true';video.pause();}});
      update(); preloader.observe(video); observer.observe(video);
    });
  }
  document.addEventListener('click', event => {
    const close = event.target.closest('[data-close-dialog]');
    if (close) closeDialog(close.closest('dialog'));
    const videoButton=event.target.closest('[data-video-open]');
    if(videoButton){
      const dialog=document.querySelector('[data-media-dialog]');
      const video=document.createElement('video');video.src=videoButton.dataset.videoFile;video.controls=true;video.playsInline=true;video.preload='metadata';video.poster=videoButton.querySelector('img')?.src || '';
      dialog.querySelector('[data-media-content]').replaceChildren(video);showDialog(dialog,videoButton);
      video.play().catch(caught=>{if(caught.name!=='AbortError' && caught.name!=='NotAllowedError')window.HearthUI.toast('This video could not play. Please try its controls.');});
    }
    const imageButton = event.target.closest('[data-image-open]');
    if (imageButton) {
      const original = imageButton.querySelector('img');
      const dialog = document.querySelector('[data-media-dialog]');
      if (!original || !dialog) return;
      const image = document.createElement('img');
      image.src = original.currentSrc || original.src;
      image.alt = original.alt;
      dialog.querySelector('[data-media-content]').replaceChildren(image);
      showDialog(dialog, imageButton);
    }
    if (event.target.matches('dialog')) {
      const rect=event.target.getBoundingClientRect();
      if (event.clientX<rect.left || event.clientX>rect.right || event.clientY<rect.top || event.clientY>rect.bottom) closeDialog(event.target);
    }
  });
  document.querySelectorAll('dialog').forEach(dialog=>dialog.addEventListener('cancel',()=>dialog.querySelectorAll('video').forEach(video=>video.pause())));
  document.addEventListener('click',event=>{
    const control=event.target.closest('[data-review-scroll]');if(!control)return;
    const track=document.getElementById(control.getAttribute('aria-controls'));if(!track)return;
    const card=track.querySelector('.review-card');
    const gap=parseFloat(getComputedStyle(track).columnGap)||0;
    track.scrollBy({left:Number(control.dataset.reviewScroll)*((card?.getBoundingClientRect().width||track.clientWidth)+gap),behavior:reducedMotion?'instant':'smooth'});
  });
  document.addEventListener('visibilitychange',()=>{document.querySelectorAll('video').forEach(video=>{if(document.hidden)video.pause();else if(video.hasAttribute('data-autoplay') && video.dataset.inView==='true' && video.dataset.userPaused!=='true' && !reducedMotion){loadLoop(video);video.play().catch(()=>{});}});});
  document.addEventListener('shopify:section:load', event=>initialise(event.target));
  initialise();
})();
